import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "components/ui/dialog";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { Download, Mail } from "lucide-react";
import { useAuth } from "AuthContext";
import { getMailOrderEmail, sendMailOrderEmail } from "actions/mailOrderAction";
import { saveOrder } from "actions/cartActions";
import { buildMailOrderExcel } from "utils/mailOrderExcel";
import { getProductPrice, formatPrice } from "utils/cartUtils";
import { getCurrencySymbol } from "utils/searchUtils";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import SamiSpin from "components/SamiSpin";

const MySwal = withReactContent(Swal);

// Every distributor with no order-placement API shares this checkout: a
// buyer-drafted email with an attached Excel of the line items. TME and
// Rutronik each have their own dedicated modal; this one is reused for the rest.
export const MAIL_ORDER_TYPES = {
  Arrow: { label: "Arrow Electronics", dataKey: "arrow_data", headerColor: "FF1B1B1B" },
  TTI: { label: "TTI", dataKey: "tti_data", headerColor: "FF0F6CBD" },
  Avnet: { label: "Avnet", dataKey: "avnet_data", headerColor: "FF5F259F" },
  // Verical is Arrow's own sub-brand (same account) — the backend routes its
  // order email lookup to Arrow's distributor row (see
  // MailOrderController::TYPES), so nothing extra is needed here.
  Verical: { label: "Verical", dataKey: "verical_data", headerColor: "FF2E7D32" },
};

const base64ToBlob = (base64, mime) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

const MailOrderModal = ({ open, modelData, setModelData, type }) => {
  const navigate = useNavigate();
  const { cart, removeByPartNumber } = useAuth();
  const config = MAIL_ORDER_TYPES[type];
  const items = useMemo(
    () => cart.filter((item) => item.api_partner?.toLowerCase() === type.toLowerCase()),
    [cart, type],
  );

  const [orderReference] = useState(`${type.toUpperCase()}-${Date.now()}`);
  const [toEmail, setToEmail] = useState("");
  const [emailLocked, setEmailLocked] = useState(false);
  const [subject, setSubject] = useState(`${config.label} Order Request - ${orderReference}`);
  const [body, setBody] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Set once the distributor has the email. From then on, Send only retries
  // recording the order: a second email would be a second order to them.
  const [sent, setSent] = useState(null);

  const currencySymbol = getCurrencySymbol(items[0]?.price?.currency);
  const totalUnits = items.reduce((a, it) => a + (parseInt(it.required_qty, 10) || 0), 0);
  const grandTotal = items.reduce(
    (a, it) => a + (parseInt(it.required_qty, 10) || 0) * getProductPrice(it),
    0,
  );

  useEffect(() => {
    if (!open) return;

    setBody(
      `Hello ${config.label} Team,\n\n` +
        `Please find attached our order request (${items.length} line item(s), ` +
        `${totalUnits} total units). The full part list with quantities and prices is in the ` +
        `attached Excel file.\n\n` +
        `Kindly confirm availability, pricing and lead time.\n\nThank you.`,
    );

    (async () => {
      try {
        const res = await getMailOrderEmail(type);
        if (res?.status && res?.data?.order_email) {
          setToEmail(res.data.order_email);
          setEmailLocked(true);
        } else {
          setEmailLocked(false);
        }
      } catch (e) {
        setEmailLocked(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type]);

  const closeModal = () => {
    setModelData({ ...modelData, open: false });
    setError(null);
  };

  const handleClose = () => {
    // Closing forgets that the email went out, and reopening would offer to
    // send it again - a duplicate order for the distributor.
    if (
      sent &&
      error &&
      !window.confirm(
        `This order was already emailed (reference ${sent.order_reference}) but is not ` +
          "recorded in My Orders. Close anyway? Do not send it again.",
      )
    )
      return;
    closeModal();
  };

  const handleDownloadExcel = async () => {
    try {
      const { base64, fileName } = await buildMailOrderExcel(items, {
        orderReference,
        distributorName: config.label,
        headerColor: config.headerColor,
      });
      const blob = base64ToBlob(
        base64,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Could not generate the Excel file.");
    }
  };

  const recordOrder = async ({ order_reference, to_email }) => {
    const bomLinks = items
      .filter((item) => item.bom_item_id)
      .map((item) => ({
        bom_item_id: item.bom_item_id,
        bom_id: item.bom_id ?? null,
        mfr_part_number: item.part_number ?? null,
        part_number: item.api_partner_part_number ?? null,
      }));

    return saveOrder({
      [config.dataKey]: {
        order_reference,
        to_email,
        subject: subject.trim(),
        cartDetailItems: items.map((item) => ({
          ...item,
          unit_price: getProductPrice(item),
        })),
      },
      bom_links: bomLinks,
    });
  };

  const handleSend = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (items.length === 0) {
        setError(`No ${config.label} items in the cart.`);
        return;
      }

      let delivery = sent;
      if (!delivery) {
        if (!emailLocked && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(toEmail).trim())) {
          setError(`Enter a valid ${config.label} order email, or configure it in distributor settings.`);
          return;
        }

        const { base64, fileName } = await buildMailOrderExcel(items, {
          orderReference,
          distributorName: config.label,
          headerColor: config.headerColor,
        });

        const res = await sendMailOrderEmail({
          type,
          subject: subject.trim(),
          body,
          to_email: emailLocked ? undefined : toEmail.trim(),
          order_reference: orderReference,
          file_name: fileName,
          excel_base64: base64,
        });

        if (!res?.status) {
          setError(res?.message || `Failed to send the ${config.label} order email.`);
          return;
        }

        delivery = {
          order_reference: res.data?.order_reference || orderReference,
          to_email: res.data?.to_email || toEmail,
        };
        setSent(delivery);
      }

      // saveOrder reports failure as { status: false }, it does not throw.
      // Clearing the cart and announcing success on a failed save left an
      // emailed order with no record in My Orders.
      let saveError = null;
      try {
        const saved = await recordOrder(delivery);
        if (!saved?.status) saveError = saved?.message || "the server did not save it";
      } catch (e) {
        saveError = e.message;
      }
      if (saveError) {
        setError(
          `The order email was sent to ${delivery.to_email} (reference ${delivery.order_reference}), ` +
            `but recording it in My Orders failed: ${saveError}. ` +
            `Retry below to record it; the email will not be sent again.`,
        );
        return;
      }

      // Not handleClose: its guard reads `error` from the render this click
      // came from, which after a failed save is still set.
      closeModal();
      items.forEach((item) => removeByPartNumber(item.part_number));

      await MySwal.fire({
        title: <strong>{config.label} Order Sent !</strong>,
        html: (
          <i>
            Order emailed to <b>{delivery.to_email}</b>. Reference{" "}
            <b>{delivery.order_reference}</b>.
          </i>
        ),
        icon: "success",
      });
      navigate("/orders");
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-primary text-lg font-semibold">
            Send {config.label} Order by Email
          </DialogTitle>
          <DialogDescription>
            {config.label} has no order API - review the draft below and send it to your {config.label} order
            email. The selected parts, quantities and prices are attached as an Excel file.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mailOrderToEmail">Order Email (To)</Label>
            <input
              id="mailOrderToEmail"
              type="email"
              value={toEmail}
              disabled={emailLocked || !!sent}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder={`Enter the ${config.label} order email`}
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20 disabled:opacity-70"
            />
            {emailLocked && (
              <span className="text-[11px] text-s360-fg3">
                Configured in distributor settings.
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mailOrderSubject">Subject</Label>
            <input
              id="mailOrderSubject"
              type="text"
              value={subject}
              disabled={!!sent}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mailOrderBody">Email Body</Label>
            <textarea
              id="mailOrderBody"
              rows={6}
              value={body}
              disabled={!!sent}
              onChange={(e) => setBody(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20"
            />
          </div>

          <div className="border border-s360-stroke1 rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-s360-bg2 flex items-center justify-between">
              <span className="text-[12px] font-semibold text-s360-violet-900">
                Attachment preview ({items.length} parts · {totalUnits} units)
              </span>
              <button
                type="button"
                onClick={handleDownloadExcel}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-s360-violet hover:underline"
              >
                <Download className="w-3.5 h-3.5" /> Download Excel
              </button>
            </div>
            <div className="overflow-x-auto max-h-52 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-[0.06em] text-s360-fg3">
                    <th className="px-3 py-2 text-left">Part Number</th>
                    <th className="px-3 py-2 text-left">Manufacturer</th>
                    <th className="px-3 py-2 text-center">Qty</th>
                    <th className="px-3 py-2 text-right">Unit Price</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => {
                    const unit = getProductPrice(item);
                    const qty = parseInt(item.required_qty, 10) || 0;
                    return (
                      <tr key={i} className="border-t border-s360-bg2">
                        <td className="px-3 py-2 font-mono text-[13px]">{item.part_number}</td>
                        <td className="px-3 py-2 text-[13px]">{item.manufacturer}</td>
                        <td className="px-3 py-2 text-center tabular-nums">{qty}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {currencySymbol} {unit}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {currencySymbol} {formatPrice(qty * unit)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-s360-stroke1 bg-[#FBFAFF]">
                    <td className="px-3 py-2 font-semibold" colSpan={4}>
                      Grand total
                    </td>
                    <td className="px-3 py-2 text-right font-bold tabular-nums">
                      {currencySymbol} {formatPrice(grandTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-500">
              Error: <span className="text-red-500">{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            className="hover:bg-gray-300 border border-gray-300"
            type="button"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button variant="primary" type="button" disabled={isLoading} onClick={handleSend}>
            {isLoading ? <SamiSpin className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {sent ? "Retry Recording Order" : "Send Order Email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MailOrderModal;
